import { FOOT_IK, type Leg } from "./footLeg";

/**
 * The foot lock's pins (FootIk.lock): which planted soles are held where they planted, and how firmly. Pure on the
 * leg's numbers, so it allocates nothing. `reach` is the leg's length in the world; (sx, sz) where the clip puts the
 * sole across the ground this frame.
 */

/** Follows the sole's step: `low` and `high` take its lift and creep toward each other between steps. */
export function trackStep(l: Leg, reach: number, dt: number): void {
  const creep = FOOT_IK.bandCreep * reach * dt;
  l.low = Math.min(l.lift, l.low + creep);
  l.high = Math.max(l.lift, l.high - creep, l.low);
}

/** How far up its own step the clip has the sole now: 0 planted, 1 at the top of the swing. */
function stepShare(l: Leg, reach: number): number {
  return (l.lift - l.low) / Math.max(FOOT_IK.minStep * reach, l.high - l.low);
}

/** Pins a planted sole (at sx, sz) while `locking`, and lets one go that the clip lifts or carries off. */
export function pinLeg(l: Leg, sx: number, sz: number, reach: number, locking: boolean): void {
  const up = stepShare(l, reach);
  if (locking && !l.pinned && up < FOOT_IK.pinShare) { l.pinned = true; l.px = sx; l.pz = sz; }
  else if (l.pinned && (up > FOOT_IK.releaseShare || Math.hypot(l.px - sx, l.pz - sz) > FOOT_IK.releaseAway * reach)) l.pinned = false;
}

/** How firmly a pin still holds (1 while planted), fading as the clip lifts the sole or carries it off the pin. */
export function holdOf(l: Leg, sx: number, sz: number, reach: number): number {
  const { pinShare: lo, releaseShare: hi, releaseAway: away, awayFade } = FOOT_IK;
  const up = Math.min(1, Math.max(0, (hi - stepShare(l, reach)) / (hi - lo)));
  const off = Math.min(1, Math.max(0, (away * reach - Math.hypot(l.px - sx, l.pz - sz)) / ((1 - awayFade) * away * reach)));
  return up * off;
}
